import { defineStore } from "pinia";
import { readonly, ref } from "vue";
import { api } from "@/loanLinkApi";
import { useFileUploadStore } from "./useFileUploadStore";
import { useInterestStore } from "./useInterestStore";
import { useLoadingStore } from "./useLoadingStore";
import { useMainStore } from "./useMainStore";
import type { Row } from "@rocicorp/zero";
import { useRouter } from "vue-router";

type DossierWithVersions = Row["dossier"] & { versions?: Row["version"] };

export const useDossierStore = defineStore("dossier", () => {
	const selectedDossier = ref<DossierWithVersions>();

	async function createDossier(dossierName: string) {
		let displayId: number | null = null;
		const loadingStore = useLoadingStore();

		try {
			loadingStore.creatingDossier = true;

			const response = await api.dossier.createDossier({ dossierName });

			if (!response.ok) {
				throw Error(response.data.message);
			}

			displayId = response.data.dossier.displayId;

			loadingStore.createDossierFailed = false;
		} catch (err) {
			switch (err?.code) {
				case "dossier/name-invalid":
					loadingStore.createDossierMessage = "Dossiernaam is ongeldig";
					break;
				case "dossier/name-too-short":
					loadingStore.createDossierMessage =
						"De dossiernaam moet tenminste 4 karakters lang zijn";
					break;
				default:
					loadingStore.createDossierMessage =
						"Dossier kon niet aangemaakt worden";
			}

			loadingStore.createDossierFailed = true;
		} finally {
			loadingStore.creatingDossier = false;
		}

		return displayId;
	}

	async function deleteDossier(dossier: Row["dossier"]) {
		const loadingStore = useLoadingStore();
		try {
			loadingStore.deletingDossier = true;

			const response = await api.dossier.deleteDossier(dossier.id);

			if (response.ok) {
				loadingStore.dossierDeleteFailed = false;
				loadingStore.deletingDossier = false;

				const router = useRouter();
				await router.push({ name: "Dossiers" });

				return true;
			}

			throw Error(response.error);
		} catch {
			loadingStore.dossierDeleteFailed = true;
		} finally {
			loadingStore.deletingDossier = false;
		}

		return false;
	}

	async function duplicateDossier(dossierId: string): Promise<number | null> {
		try {
			const response = await api.request<{
				message: string;
				dossier: { displayId: number };
			}>({
				path: `/dossier/${dossierId}/duplicate`,
				method: "POST",
				secure: true,
				format: "json",
			});

			return response.data.dossier.displayId;
		} catch {
			return null;
		}
	}

	function setSelected(dossier?: DossierWithVersions) {
		selectedDossier.value = dossier;

		const interestStore = useInterestStore();
		interestStore.$reset();

		const store = useMainStore();
		store.showOptimalHeights = false;

		const fileUploadStore = useFileUploadStore();
		fileUploadStore.clearFilesToUpload();
		fileUploadStore.selectedFromExistingFiles = [];
		fileUploadStore.lastSelectedFile = undefined;
	}

	return {
		selectedDossier: readonly(selectedDossier),
		setSelected,

		createDossier,
		deleteDossier,
		duplicateDossier,
	};
});
